import React from 'react';
import { MANTRA_PRESETS } from './MantraRoleConfiguration';

export const ROLE_ORDER = ['P', 'D', 'C', 'A'];

export const ROLE_LABELS = {
  P: 'Portieri',
  D: 'Difensori',
  C: 'Centrocampisti',
  A: 'Attaccanti',
};

export const FORMATIONS = {
  '3-4-3': { P: 1, D: 3, C: 4, A: 3 },
  '3-5-2': { P: 1, D: 3, C: 5, A: 2 },
  '4-3-3': { P: 1, D: 4, C: 3, A: 3 },
  '4-4-2': { P: 1, D: 4, C: 4, A: 2 },
  '5-3-2': { P: 1, D: 5, C: 3, A: 2 },
};

export const SORT_OPTIONS = [
  { value: 'value_desc', label: 'Valore ↓' },
  { value: 'value_asc', label: 'Valore ↑' },
  { value: 'mv_desc', label: 'MV ↓' },
  { value: 'fmv_desc', label: 'FV ↓' },
  { value: 'caps_desc', label: 'Partite ↓' },
  { value: 'goals_desc', label: 'Gol ↓' },
  { value: 'assists_desc', label: 'Assist ↓' },
  { value: 'tier_asc', label: 'Fascia' },
  { value: 'hidden_gems', label: 'Perle nascoste' },
  { value: 'pma_delta_desc', label: 'Delta PMA-Prezzo ↓' },
  { value: 'playprob_desc', label: 'Probabilità impiego ↓' },
  { value: 'name', label: 'Nome' },
  { value: 'new_first', label: 'Nuovi prima' },
];

export const ROLE_CHIP_THEME = {
  P: { backgroundColor: '#f5c542', color: '#111827' },
  D: { backgroundColor: '#20c55e', color: '#ffffff' },
  C: { backgroundColor: '#3b82f6', color: '#ffffff' },
  A: { backgroundColor: '#ef4444', color: '#ffffff' },
};

export function v(x) {
  return Number(x || 0);
}

export function fmt(x) {
  const n = Number(x || 0);
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
}

// ─── Stagione corrente vs precedente ──────────────────────────────────────
// A inizio campionato (o comunque con pochissime presenze quest'anno) le
// stat correnti sono quasi tutte a zero: i punteggi che le usano con un peso
// fisso "sprecano" quel peso invece di spostarlo sullo storico dell'anno
// prima. FULL_CONFIDENCE_CAPS presenze in stagione corrente = i dati di
// quest'anno sono considerati abbastanza robusti da pesare per intero.
const FULL_CONFIDENCE_CAPS = 8;

export function getCurrentSeasonWeight(currentCaps) {
  return clampNumber(v(currentCaps) / FULL_CONFIDENCE_CAPS, 0, 1);
}

// Dati due pesi massimi (per stagione corrente e precedente, validi quando
// currentCaps >= FULL_CONFIDENCE_CAPS — stesso risultato di prima, invariato),
// restituisce i pesi effettivi da usare: il peso "corrente" non ancora
// guadagnato (perché ci sono poche o zero presenze quest'anno) viene
// spostato sul precedente invece di andare perso su un valore vicino a zero.
export function getSeasonBlendWeights(currentMaxWeight, previousMaxWeight, currentCaps) {
  const seasonWeight = getCurrentSeasonWeight(currentCaps);
  const currentWeight = currentMaxWeight * seasonWeight;
  const previousWeight = previousMaxWeight + (currentMaxWeight - currentWeight);
  return { currentWeight, previousWeight };
}

// ─── Probabilità di impiego (probabili formazioni) ───────────────────────────
// entry ha forma { status: 'starter'|'bench', prob: number|null } (vedi
// getLineupStatusByPlayerId in firebase/playerService.js). Usato sia dal Market
// che dagli Svincolati per mostrare/ordinare lo stesso dato in modo coerente.
export function getPlayProbabilityLabel(entry) {
  const prob = Number(entry?.prob);
  if (!entry || !Number.isFinite(prob)) return '—';
  return entry.status === 'starter' ? `Titolare (${prob}%)` : `Panchina (${prob}%)`;
}

export function getPlayProbabilitySortValue(entry) {
  const prob = Number(entry?.prob);
  return Number.isFinite(prob) ? prob : -1;
}

export function getInitials(name = '') {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');
}

export function Metric({ label, value }) {
  return (
    <div className="ad-metric">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

export function RoleChip({ role }) {
  const safeRole = ROLE_ORDER.includes(role) ? role : 'A';
  const label = ROLE_LABELS[safeRole] || safeRole;
  const theme = ROLE_CHIP_THEME[safeRole] || ROLE_CHIP_THEME.A;

  return (
    <span
      className={`auction-role-chip role-chip role-chip--${safeRole}`}
      style={theme}
      aria-label={label}
    >
      {safeRole}
    </span>
  );
}

export function AuctionTeamAvatar({ name }) {
  return <div className="auction-team-avatar">{getInitials(name)}</div>;
}

// Le probabili formazioni FantaMaster (vedi getLineupStatusByPlayerId in
// firebase/playerService.js) distinguono solo titolare (prob 90) da panchina
// (prob 30): usiamo quel segnale come una nudge contenuta (±15%), non come
// fattore dominante — la stagione intera pesa comunque di più di una singola
// giornata. Nessun dato disponibile → fattore neutro (1), non una penalità.
export function getPlayProbabilityScoreFactor(entry) {
  const prob = Number(entry?.prob);
  if (!entry || !Number.isFinite(prob)) return 1;
  return Math.max(0.85, Math.min(1.15, 0.85 + (prob / 100) * 0.3));
}

export function calcPlayerScore(purchase) {
  const p = purchase?.playerSnapshot || {};
  const role = p.role || purchase?.role;
  const prev = p.previousSeasonStats || {};
  const currentCaps = v(p.caps);
  const probFactor = getPlayProbabilityScoreFactor(p.playProbability);

  // A inizio stagione (poche presenze) fmarkavg/markavg/gol/assist correnti
  // sono quasi a zero: senza fallback un top player con caps bassi risulta
  // "debole" quanto un giocatore mediocre. Questo punteggio guida anche il
  // motore scambi (chi è un candidato realistico da offrire/ricevere), quindi
  // l'effetto concreto era proposte irrealistiche a inizio stagione (es. un
  // top scorer che sembra alla pari con un giocatore di rincalzo). Stesso
  // meccanismo già usato per il prezzo suggerito in asta.
  const fmvWeights = getSeasonBlendWeights(3, 0, currentCaps);
  const mvWeights = getSeasonBlendWeights(1.8, 0, currentCaps);
  const goalsWeights = getSeasonBlendWeights(4, 0, currentCaps);
  const assistsWeights = getSeasonBlendWeights(3, 0, currentCaps);

  const base =
    v(p.value) * 1.2 +
    v(p.fmarkavg) * fmvWeights.currentWeight + v(prev.fmarkavg) * fmvWeights.previousWeight +
    v(p.markavg) * mvWeights.currentWeight + v(prev.markavg) * mvWeights.previousWeight +
    v(p.goals) * goalsWeights.currentWeight + v(prev.goals) * goalsWeights.previousWeight +
    v(p.assists) * assistsWeights.currentWeight + v(prev.assists) * assistsWeights.previousWeight +
    v(p.caps) * 0.2 -
    v(p.ycards) * 0.4 -
    v(p.rcards) * 1.8 -
    v(p.owngoals) * 1.6;

  if (role === 'P') {
    return (base - v(p.gotgoals) * 0.18) * probFactor;
  }

  return base * probFactor;
}

export function hiddenGemScore(player) {
  const prev = player?.previousSeasonStats || {};
  return (
    v(prev.fmarkavg) * 4 +
    v(prev.markavg) * 3 +
    v(prev.goals) * 5 +
    v(prev.assists) * 4 +
    v(prev.caps) * 0.35 +
    v(prev.value) * 0.25 +
    v(player?.markavg) * 0.8 +
    v(player?.fmarkavg) * 1.1 -
    v(player?.value) * 0.9
  );
}

export function estimateAuctionPrice(player, teamCount) {
  const base = v(player.value);

  const qualityBoost =
    v(player.fmarkavg) * 2 +
    v(player.markavg) * 1.5 +
    v(player.goals) * 2;

  const competitionFactor = 1 + Math.max(teamCount - 1, 0) * 0.08;

  return base * competitionFactor + qualityBoost;
}

function getCaps(player) {
  return v(player?.caps);
}

function getPrevCaps(player) {
  return v(player?.previousSeasonStats?.caps);
}

function getCurrentFmv(player) {
  return v(player?.fmarkavg);
}

function getPrevFmv(player) {
  return v(player?.previousSeasonStats?.fmarkavg);
}

function getCurrentMv(player) {
  return v(player?.markavg);
}

function getPrevMv(player) {
  return v(player?.previousSeasonStats?.markavg);
}

function buildAvailabilityScore(player) {
  const currentCaps = getCaps(player);
  const prevCaps = getPrevCaps(player);

  return currentCaps * 1.15 + prevCaps * 0.65;
}

function buildReliabilityScore(player) {
  const currentCaps = getCaps(player);
  const prevCaps = getPrevCaps(player);

  const currentFmv = getCurrentFmv(player);
  const prevFmv = getPrevFmv(player);
  const currentMv = getCurrentMv(player);
  const prevMv = getPrevMv(player);

  const currentWeight = currentCaps > 0 ? 1 : 0.45;
  const prevWeight = prevCaps > 0 ? 1 : 0.15;

  return (
    currentFmv * 3.2 * currentWeight +
    currentMv * 1.8 * currentWeight +
    prevFmv * 2.4 * prevWeight +
    prevMv * 1.4 * prevWeight
  );
}

function buildUpsideScore(player) {
  const value = v(player?.value);
  const currentFmv = getCurrentFmv(player);
  const prevFmv = getPrevFmv(player);
  const currentCaps = getCaps(player);
  const prevCaps = getPrevCaps(player);

  const lowCostBonus = Math.max(0, 18 - value) * 0.9;
  const formBonus = Math.max(currentFmv, prevFmv) * 1.4;
  const sampleBonus = Math.min(currentCaps + prevCaps, 40) * 0.18;

  return lowCostBonus + formBonus + sampleBonus;
}

function hasPlayableHistory(player) {
  // Giocatori con presenze o con un valore di mercato significativo (noti e schierabili)
  return getAiPlayerCaps(player) > 0 || getAiPrevCaps(player) > 0 || getAiPlayerValue(player) >= 8;
}

const AI_ELITE_KEEPER_TEAMS = new Set(['inter', 'juventus', 'milan', 'roma', 'napoli']);

const AI_TEAM_BONUS_COEFF = {
  inter: 1.4,
  milan: 1.14,
  juventus: 1.13,
  napoli: 1.12,
  roma: 1.1,
  atalanta: 1.1,
  bologna: 1.06,
  lazio: 1.05,
  fiorentina: 1.05,
  torino: 1.03,
  como: 1.1,
  else: 1.0,
};

function clampNumber(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function normalizeTeamKey(teamName = '') {
  return String(teamName)
    .toLowerCase()
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z]/g, '');
}

function getAiTeamBonusCoeff(teamName = '') {
  const key = normalizeTeamKey(teamName);
  return AI_TEAM_BONUS_COEFF[key] ?? AI_TEAM_BONUS_COEFF.else ?? 1;
}

function getAiPlayerTeam(player) {
  return player?.team || player?.playerTeam || player?.playerSnapshot?.team || '—';
}

function getAiPlayerValue(player) {
  return v(
    player?.value ??
    player?.pma ??
    player?.pmaCurrent ??
    player?.averagePma ??
    player?.playerSnapshot?.value ??
    player?.playerSnapshot?.pma ??
    player?.playerSnapshot?.pmaCurrent ??
    player?.playerSnapshot?.averagePma ??
    0
  );
}

function getAiPlayerCaps(player) {
  return v(player?.caps ?? player?.playerSnapshot?.caps ?? 0);
}

function getAiPrevCaps(player) {
  return v(player?.previousSeasonStats?.caps ?? 0);
}

function getAiPlayerFmv(player) {
  return v(player?.fmarkavg ?? player?.playerSnapshot?.fmarkavg ?? 0);
}

function getAiPrevFmv(player) {
  return v(player?.previousSeasonStats?.fmarkavg ?? 0);
}

function getAiPlayerMv(player) {
  return v(player?.markavg ?? player?.playerSnapshot?.markavg ?? 0);
}

function getAiPrevMv(player) {
  return v(player?.previousSeasonStats?.markavg ?? 0);
}

function splitRoleTokens(role = '') {
  return String(role)
    .split(/[/,|;]/)
    .flatMap(part => part.split(/\s+/))
    .map(token => token.trim())
    .filter(Boolean);
}

export function getAiRoleFamily(player, rolesMode = 'classic') {
  const roleValue = String(player?.role || player?.playerSnapshot?.role || '').trim();

  if (rolesMode === 'mantra') {
    const tokens = splitRoleTokens(roleValue)
      .map((t) => String(t || '').trim().toUpperCase())
      .filter(Boolean);

    if (tokens.includes('P')) return 'P';
    if (tokens.some((t) => ['DD', 'DC', 'DS', 'B'].includes(t))) return 'D';
    if (tokens.some((t) => ['E', 'M', 'C', 'W', 'T'].includes(t))) return 'C';
    if (tokens.some((t) => ['A', 'PC'].includes(t))) return 'A';
    return 'C';
  }

  const upper = roleValue.toUpperCase();
  if (upper === 'P') return 'P';
  if (['D', 'DD', 'DC', 'DS', 'B'].includes(upper)) return 'D';
  if (['C', 'E', 'M', 'W', 'T'].includes(upper)) return 'C';
  return 'A';
}

function isAiGoalkeeper(player, rolesMode = 'classic') {
  return getAiRoleFamily(player, rolesMode) === 'P';
}

export function getAiPerformanceScore(player, roleFamily) {
  if (!player) return 0;

  const currentCaps = getAiPlayerCaps(player);
  const prevCaps = getAiPrevCaps(player);
  const currentFmv = getAiPlayerFmv(player);
  const prevFmv = getAiPrevFmv(player);
  const currentMv = getAiPlayerMv(player);
  const prevMv = getAiPrevMv(player);

  const goals = v(player?.goals ?? player?.playerSnapshot?.goals ?? 0);
  const prevGoals = v(player?.previousSeasonStats?.goals ?? 0);
  const assists = v(player?.assists ?? player?.playerSnapshot?.assists ?? 0);
  const prevAssists = v(player?.previousSeasonStats?.assists ?? 0);
  const goalsAgainst = v(
    player?.gotgoals ??
    player?.goalsConceded ??
    player?.goals_against ??
    player?.playerSnapshot?.gotgoals ??
    player?.playerSnapshot?.goalsConceded ??
    player?.playerSnapshot?.goals_against ??
    0
  );
  const prevGoalsAgainst = v(player?.previousSeasonStats?.gotgoals ?? 0);
  // I rigori parati non vengono salvati per la stagione precedente (il dato
  // sincronizzato non li include), quindi qui restano solo sul valore corrente.
  const saves = v(
    player?.savedPenalties ??
    player?.penaltiesSaved ??
    player?.penalties_saved ??
    player?.saves ??
    player?.playerSnapshot?.savedPenalties ??
    player?.playerSnapshot?.penaltiesSaved ??
    player?.playerSnapshot?.penalties_saved ??
    player?.playerSnapshot?.saves ??
    0
  );
  const yc = v(player?.ycards ?? player?.playerSnapshot?.ycards ?? 0);
  const rc = v(player?.rcards ?? player?.playerSnapshot?.rcards ?? 0);
  const ownGoals = v(player?.owngoals ?? player?.playerSnapshot?.owngoals ?? 0);

  const availability = buildAvailabilityScore(player);
  const reliability = buildReliabilityScore(player);
  const upside = buildUpsideScore(player);

  // Gol/assist/gol subiti contavano solo la stagione corrente: a inizio
  // campionato (0 presenze) restavano sempre a zero, ignorando del tutto un
  // attaccante che l'anno scorso ha segnato più di tutti. Stesso principio
  // di ridistribuzione del peso usato per formPulse/experiencePulse sotto.
  const { currentWeight: statWeight, previousWeight: prevStatWeight } =
    getSeasonBlendWeights(1, 0, currentCaps);
  const blendedGoals = (goals * statWeight) + (prevGoals * prevStatWeight);
  const blendedAssists = (assists * statWeight) + (prevAssists * prevStatWeight);
  const blendedGoalsAgainst = (goalsAgainst * statWeight) + (prevGoalsAgainst * prevStatWeight);

  const production =
    roleFamily === 'P'
      ? (saves * 4.8) - (blendedGoalsAgainst * 0.55)
      : roleFamily === 'D'
        ? (blendedGoals * 5.1) + (blendedAssists * 3.1)
        : roleFamily === 'C'
          ? (blendedGoals * 4.0) + (blendedAssists * 4.0)
          : (blendedGoals * 5.6) + (blendedAssists * 4.5);

  const { currentWeight: fmvWeight, previousWeight: prevFmvWeight } =
    getSeasonBlendWeights(0.55, 0.12, currentCaps);
  const { currentWeight: mvWeight, previousWeight: prevMvWeight } =
    getSeasonBlendWeights(0.25, 0.08, currentCaps);
  const formPulse =
    (currentFmv * fmvWeight) + (prevFmv * prevFmvWeight) +
    (currentMv * mvWeight) + (prevMv * prevMvWeight);

  const { currentWeight: expWeight, previousWeight: prevExpWeight } =
    getSeasonBlendWeights(0.7, 0.3, currentCaps);
  const experiencePulse = (currentCaps * expWeight) + (prevCaps * prevExpWeight);

  const disciplinePenalty = (yc * 0.28) + (rc * 1.35) + (ownGoals * 1.15);

  return Math.max(
    0,
    (availability * 0.9) +
    (reliability * 0.7) +
    (upside * 0.5) +
    production +
    (formPulse * 1.4) +
    (experiencePulse * 0.35) -
    disciplinePenalty
  );
}

function getAiPriceEstimate(player, roleFamily, isSecondKeeperTarget = false) {
  const currentValue = getAiPlayerValue(player);
  const previousValue = v(player?.previousSeasonStats?.value ?? 0);

  const fallbackValue = {
    P: 2.5,
    D: 3.5,
    C: 4.5,
    A: 5.5,
  };

  const anchorValue =
    currentValue > 0
      ? currentValue
      : previousValue > 0
        ? previousValue
        : fallbackValue[roleFamily] || 3;

  const teamCoeff = getAiTeamBonusCoeff(getAiPlayerTeam(player));
  const currentCaps = getAiPlayerCaps(player);
  const prevCaps = getAiPrevCaps(player);

  const valuePulse = clampNumber(Math.log1p(anchorValue) / Math.log1p(120), 0, 1);
  const eliteValueFactor = clampNumber(Math.pow(valuePulse, 1.75), 0, 1);
  const { currentWeight: availCurrentWeight, previousWeight: availPrevWeight } =
    getSeasonBlendWeights(0.7, 0.3, currentCaps);
  const availabilityIndex = clampNumber(
    (currentCaps * availCurrentWeight + prevCaps * availPrevWeight) / 25,
    0,
    1
  );

  const performanceScore = getAiPerformanceScore(player, roleFamily);
  const performancePulse = clampNumber(performanceScore / 35, 0, 1);

  const roleLift = {
    P: 0.95,
    D: 1.0,
    C: 1.08,
    A: 1.15,
  }[roleFamily] || 1.0;

  const teamLift = clampNumber(0.95 + (teamCoeff - 1) * 0.9, 0.95, 1.18);
  const availabilityLift = 0.35 + (availabilityIndex * 0.65);
  const valueLiftStrong = 0.55 + (eliteValueFactor * 0.85);
  const performanceLift =
    1 + (performancePulse - 0.35) * (roleFamily === 'A' ? 0.2 : roleFamily === 'C' ? 0.18 : roleFamily === 'D' ? 0.14 : 0.1);

  let estimatedPrice = Math.max(
    1,
    Math.round(
      anchorValue *
      (0.8 + eliteValueFactor * 1.05) *
      roleLift *
      teamLift *
      valueLiftStrong *
      performanceLift *
      availabilityLift
    )
  );

  if (isSecondKeeperTarget) {
    estimatedPrice = Math.max(1, Math.round(estimatedPrice * 0.68));
  }

  return {
    estimatedPrice,
    anchorValue,
    currentCaps,
    previousCaps: prevCaps,
    valuePulse,
    eliteValueFactor,
    availabilityIndex,
    performanceScore,
    teamCoeff,
  };
}

// ─── Segnaposto portieri di riserva ──────────────────────────────────────────
// Quando possiedo (o compro) il portiere con quota più alta di una squadra top
// ma le sue riserve non sono identificabili con certezza (più di due portieri
// liberi con quota simile), suggerisco un segnaposto "Secondo/Terzo portiere
// <squadra>" da 1 credito, senza pulsante di acquisto.

const KEEPER_ORDINAL_LABELS = { 2: 'Secondo', 3: 'Terzo', 4: 'Quarto', 5: 'Quinto' };

function buildKeeperPlaceholderSuggestion(ordinal, teamName) {
  const label = KEEPER_ORDINAL_LABELS[ordinal] || 'Riserva';
  return {
    id: `keeper-placeholder-${ordinal}-${normalizeTeamKey(teamName)}`,
    name: `${label} portiere ${teamName}`,
    team: teamName,
    role: 'P',
    currentCaps: 0,
    previousCaps: 0,
    value: 1,
    fmarkavg: 0,
    estimatedPrice: 1,
    isPlaceholder: true,
    planPriority: 3,
    planNote: 'Riserva P',
  };
}

// Portieri delle squadre top raggruppati per squadra e ordinati per quota
// (indice 0 = titolare). Condiviso da mantra e classic.
function buildEliteKeeperRanking(players = []) {
  const byTeam = new Map();
  (players || [])
    .filter((p) => !p?.deleted)
    .forEach((p) => {
      if (!isAiGoalkeeper(p, 'classic') && !isAiGoalkeeper(p, 'mantra')) return;
      const teamKey = normalizeTeamKey(getAiPlayerTeam(p));
      if (!AI_ELITE_KEEPER_TEAMS.has(teamKey)) return;
      if (!byTeam.has(teamKey)) byTeam.set(teamKey, []);
      byTeam.get(teamKey).push(p);
    });
  byTeam.forEach((list) => list.sort((a, b) => getAiPlayerValue(b) - getAiPlayerValue(a)));
  return byTeam;
}

function isTopEliteKeeperName(ranking, teamKey, name) {
  const top = ranking.get(teamKey)?.[0];
  return Boolean(top) && normalizeLookupKey(top.name || '') === normalizeLookupKey(name || '');
}

// Riserve (2°, 3°, …) di una squadra top ancora libere all'asta
function eliteKeeperFreeBackups(ranking, teamKey, ownedIndex) {
  return (ranking.get(teamKey) || [])
    .slice(1)
    .filter((p) => !isOwnedPlayer(p, ownedIndex));
}

// Squadra top di cui possiedo il portiere con quota più alta (se esiste)
function getMyEliteKeeperAnchor({ players = [], purchases = [], myRosterPurchases = [] }) {
  const ranking = buildEliteKeeperRanking(players);
  const myIndex = buildOwnedPlayerIndex(myRosterPurchases);
  const allIndex = buildOwnedPlayerIndex(purchases);

  for (const [teamKey, list] of ranking) {
    const top = list[0];
    if (!top || !isOwnedPlayer(top, myIndex)) continue;

    const backups = list.slice(1);
    return {
      teamKey,
      teamName: getAiPlayerTeam(top),
      backups,
      freeBackups: backups.filter((p) => !isOwnedPlayer(p, allIndex)),
    };
  }

  return null;
}

function normalizeLookupKey(value = '') {
  return String(value)
    .toLowerCase()
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '');
}

function buildOwnedPlayerIndex(purchases = []) {
  const ids = new Set();
  const keys = new Set();

  (purchases || [])
    .filter((p) => !p?.deleted)
    .forEach((purchase) => {
      const snap = purchase?.playerSnapshot || {};

      [
        purchase.playerId,
        purchase.id,
        snap.id,
        snap.playerId,
        snap.player_id,
        snap.playerID,
      ]
        .filter(Boolean)
        .forEach((id) => ids.add(String(id)));

      const nameKey = normalizeLookupKey(snap.name || purchase.playerName || '');
      const teamKey = normalizeTeamKey(snap.team || purchase.playerTeam || '');

      if (nameKey) {
        keys.add(`${nameKey}|${teamKey}`);
      }
    });

  return { ids, keys };
}

function isOwnedPlayer(player, ownedIndex) {
  const idsToCheck = [
    player?.id,
    player?.playerId,
    player?.playerSnapshot?.id,
    player?.playerSnapshot?.playerId,
  ]
    .filter(Boolean)
    .map(String);

  if (idsToCheck.some((id) => ownedIndex.ids.has(id))) return true;

  const nameKey = normalizeLookupKey(
    player?.name || player?.playerName || player?.playerSnapshot?.name || ''
  );
  const teamKey = normalizeTeamKey(
    player?.team || player?.playerTeam || player?.playerSnapshot?.team || ''
  );

  return Boolean(nameKey) && ownedIndex.keys.has(`${nameKey}|${teamKey}`);
}

// ─── SUGGERIMENTI MANTRA ──────────────────────────────────────────────────────
// In modalità mantra il suggerimento è un vero piano d'acquisto: parte dai
// crediti residui, usa la quota (valutazione) come prezzo, punta a completare
// il modulo più coperto dalla rosa e garantisce riserve compatibili in panchina
// (regolamento: https://www.fantacalcio.it/regolamenti/sistema-mantra).

const MANTRA_CANONICAL_TOKEN_MAP = {
  P: 'P', DD: 'Dd', DC: 'Dc', DS: 'Ds', B: 'B', E: 'E',
  M: 'M', C: 'C', W: 'W', T: 'T', A: 'A', PC: 'Pc',
};

function normalizeMantraToken(token = '') {
  const key = String(token || '').trim().toUpperCase();
  return MANTRA_CANONICAL_TOKEN_MAP[key] || String(token || '').trim();
}

function getMantraTokens(entry) {
  const roleValue = entry?.playerSnapshot?.role ?? entry?.role ?? '';
  return [...new Set(splitRoleTokens(roleValue).map(normalizeMantraToken).filter(Boolean))];
}

// Moduli target: i più utilizzati (massimizzano i giocatori offensivi in campo).
// La squadra viene strutturata per poter giocare questi moduli.
const MANTRA_TARGET_MODULE_KEYS = ['4231', '4411', '4141', '433', '3421'];

// Massimo numero di Pc (centravanti puri) in rosa: i moduli mantra ne schierano
// al più 2, quindi averne di più è spreco.
const MANTRA_PC_MAX = 2;

// Un candidato "occupa" uno slot da centravanti puro se tra i suoi ruoli c'è Pc
const isPcCandidate = (candidate) => candidate.tokens.includes('Pc');

// Matching bipartito (augmenting path): quanti slot del modulo sono coperti
// dai giocatori disponibili, ognuno usabile per un solo slot.
function matchMantraSlots(playerTokenSets, flatSlots) {
  if (!flatSlots.length || !playerTokenSets.length) {
    return {
      matched: 0,
      matchedMask: flatSlots.map(() => false),
      usedPlayers: new Array(playerTokenSets.length).fill(false),
    };
  }

  const slotData = flatSlots
    .map((accepted, idx) => ({
      idx,
      candidates: playerTokenSets
        .map((tokens, pi) => (accepted.some((tok) => tokens.includes(tok)) ? pi : -1))
        .filter((pi) => pi >= 0),
    }))
    .sort((a, b) => a.candidates.length - b.candidates.length);

  const matchPlayer = new Array(playerTokenSets.length).fill(-1);

  function augment(si, seen) {
    for (const pi of slotData[si].candidates) {
      if (seen[pi]) continue;
      seen[pi] = true;
      const prev = matchPlayer[pi];
      if (prev === -1 || augment(prev, seen)) {
        matchPlayer[pi] = si;
        return true;
      }
    }
    return false;
  }

  let matched = 0;
  for (let s = 0; s < slotData.length; s++) {
    if (augment(s, new Array(playerTokenSets.length).fill(false))) matched++;
  }

  const matchedOriginalIdxs = new Set();
  matchPlayer.forEach((si) => {
    if (si !== -1) matchedOriginalIdxs.add(slotData[si].idx);
  });

  return {
    matched,
    matchedMask: flatSlots.map((_, i) => matchedOriginalIdxs.has(i)),
    usedPlayers: matchPlayer.map((si) => si !== -1),
  };
}

export function getMantraSmartSuggestions({
  players = [],
  purchases = [],
  myRosterPurchases = [],
  teamStats = {},
} = {}) {
  const suggestions = { P: [], M: [] };

  const budget = Math.max(v(teamStats?.remaining), 0);
  const slotsP = Math.max(v(teamStats?.remainingByRole?.P), 0);
  const slotsM = Math.max(v(teamStats?.remainingByRole?.M), 0);
  if (slotsP + slotsM <= 0 || budget <= 0) return suggestions;

  const ownedIndex = buildOwnedPlayerIndex(purchases);

  const rosterTokenSets = (myRosterPurchases || [])
    .filter((p) => !p?.deleted)
    .map(getMantraTokens)
    .filter((tokens) => tokens.length);
  const movementRoster = rosterTokenSets.filter((tokens) => !tokens.includes('P'));
  const ownedKeepers = rosterTokenSets.length - movementRoster.length;

  // ── Candidati liberi: la quota è il prezzo d'acquisto assunto ──────────────
  const freeAgents = (players || [])
    .filter((p) => !p?.deleted)
    .filter((p) => !isOwnedPlayer(p, ownedIndex))
    .map((p) => {
      const tokens = getMantraTokens(p);
      if (!tokens.length) return null;

      // I portieri di riserva (0 presenze, quota 1) restano candidati validi:
      // servono per la strategia "porta di una squadra top"
      if (!tokens.includes('P') && !hasPlayableHistory(p)) return null;

      const family = getAiRoleFamily(p, 'mantra');
      const value = getAiPlayerValue(p);
      const price = Math.max(1, Math.round(value));
      const currentCaps = getAiPlayerCaps(p);
      const previousCaps = getAiPrevCaps(p);
      const fmv = getAiPlayerFmv(p);
      const prevFmv = getAiPrevFmv(p);
      const mv = getAiPlayerMv(p);
      const prevMv = getAiPrevMv(p);
      const perf = getAiPerformanceScore(p, family);

      // fmv/mv non avevano un fallback sulla stagione precedente: a inizio
      // campionato (0 presenze) valevano sempre 0, ignorando chi l'anno
      // scorso era tra i migliori. Il peso "corrente" non guadagnato per
      // mancanza di presenze si sposta sullo storico.
      const { currentWeight: fmvWeight, previousWeight: prevFmvWeight } =
        getSeasonBlendWeights(14, 0, currentCaps);
      const { currentWeight: mvWeight, previousWeight: prevMvWeight } =
        getSeasonBlendWeights(6, 0, currentCaps);
      const { currentWeight: benchFmvWeight, previousWeight: benchPrevFmvWeight } =
        getSeasonBlendWeights(8, 0, currentCaps);

      // Titolari: conta la qualità assoluta (più i multiruolo, preziosi nel mantra)
      const strongScore =
        (perf * 2.4) +
        (currentCaps * 10) + (previousCaps * 4) +
        (fmv * fmvWeight) + (prevFmv * prevFmvWeight) +
        (mv * mvWeight) + (prevMv * prevMvWeight) +
        (Math.min(value, 80) * 1.2) +
        (tokens.length * 15);

      // Riserve: gioca tanto, costa poco, copre più ruoli possibile
      const benchScore =
        (currentCaps * 30) + (previousCaps * 12) +
        (fmv * benchFmvWeight) + (prevFmv * benchPrevFmvWeight) + (perf * 0.8) +
        (tokens.length * 20) -
        (price * 10);

      // Probabile titolare/panchina nella prossima giornata: piccola spinta o
      // freno su entrambi i punteggi (vedi getPlayProbabilityScoreFactor).
      const probFactor = getPlayProbabilityScoreFactor(p.playProbability);

      return {
        id: p.id,
        name: p.name || p.playerName || '—',
        team: getAiPlayerTeam(p),
        role: p.role || p.playerSnapshot?.role,
        tokens,
        isKeeper: tokens.includes('P'),
        price,
        value,
        currentCaps,
        previousCaps,
        fmarkavg: fmv,
        strongScore: strongScore * probFactor,
        benchScore: benchScore * probFactor,
      };
    })
    .filter(Boolean);

  const movementAgents = freeAgents.filter((c) => !c.isKeeper);
  const keeperAgents = freeAgents.filter((c) => c.isKeeper);

  // ── Strategia portieri: se prendo (o ho già) il primo portiere per quota di
  //    una squadra top, come riserve suggerisco il 2° e 3° portiere per quota
  //    della stessa squadra: porta sempre coperta risparmiando crediti ─────────
  const eliteKeepersByTeam = new Map();
  (players || [])
    .filter((p) => !p?.deleted)
    .forEach((p) => {
      if (!getMantraTokens(p).includes('P')) return;
      const teamKey = normalizeTeamKey(getAiPlayerTeam(p));
      if (!AI_ELITE_KEEPER_TEAMS.has(teamKey)) return;
      if (!eliteKeepersByTeam.has(teamKey)) eliteKeepersByTeam.set(teamKey, []);
      eliteKeepersByTeam.get(teamKey).push(p);
    });
  eliteKeepersByTeam.forEach((list) => list.sort((a, b) => getAiPlayerValue(b) - getAiPlayerValue(a)));

  const isTopEliteKeeper = (name, teamKey) => {
    const top = eliteKeepersByTeam.get(teamKey)?.[0];
    return Boolean(top) && normalizeLookupKey(top.name || '') === normalizeLookupKey(name || '');
  };

  // Possiedo già il portiere titolare di una squadra top?
  let keeperAnchorTeamKey = null;
  (myRosterPurchases || [])
    .filter((p) => !p?.deleted)
    .forEach((purchase) => {
      if (!getMantraTokens(purchase).includes('P')) return;
      const snap = purchase?.playerSnapshot || {};
      const teamKey = normalizeTeamKey(snap.team || purchase.playerTeam || '');
      if (isTopEliteKeeper(snap.name || purchase.playerName || '', teamKey)) {
        keeperAnchorTeamKey = teamKey;
      }
    });

  // Il primo portiere di una squadra top vale di più: sblocca riserve low-cost
  keeperAgents.forEach((c) => {
    if (isTopEliteKeeper(c.name, normalizeTeamKey(c.team))) c.strongScore += 400;
  });

  // ── Modulo target: tra i moduli più usati, quello già più coperto dalla rosa ─
  const moduleStates = MANTRA_TARGET_MODULE_KEYS.map((key) => {
    const preset = MANTRA_PRESETS[key];
    const movementSlots = preset.rows
      .filter((row) => row.key !== 'P')
      .flatMap((row) => row.slots);
    return { key, label: preset.label, movementSlots, match: matchMantraSlots(movementRoster, movementSlots) };
  });

  moduleStates.sort((a, b) => {
    const missingA = a.movementSlots.length - a.match.matched;
    const missingB = b.movementSlots.length - b.match.matched;
    if (missingA !== missingB) return missingA - missingB;
    return MANTRA_TARGET_MODULE_KEYS.indexOf(a.key) - MANTRA_TARGET_MODULE_KEYS.indexOf(b.key);
  });

  const target = moduleStates[0];

  // ── Obiettivo di copertura ──────────────────────────────────────────────────
  // La rosa (posseduti + suggeriti) deve poter schierare TUTTI i moduli target e
  // reggere la perdita di un titolare: quindi serve profondità con giocatori
  // DISTINTI su ogni ruolo, non accumulare attaccanti. Misuro la robustezza con
  // due matching per modulo: uno sugli slot singoli (schierabilità) e uno sugli
  // slot raddoppiati (una riserva distinta per ogni slot).
  const targetModulesSlots = moduleStates.map((ms) => ms.movementSlots);
  const targetModulesDoubled = targetModulesSlots.map((slots) => [...slots, ...slots]);

  const robustness = (sets) => {
    let total = 0;
    for (let mi = 0; mi < targetModulesSlots.length; mi++) {
      const slots = targetModulesSlots[mi];
      const single = matchMantraSlots(sets, slots).matched;
      const dbl = matchMantraSlots(sets, targetModulesDoubled[mi]).matched;
      total += single * 40;
      if (single === slots.length) total += 150; // modulo schierabile
      total += dbl * 8;                           // profondità con giocatori distinti
    }
    return total;
  };

  const pickedIds = new Set();
  let pcCount = movementRoster.filter((t) => t.includes('Pc')).length;
  let remainingBudget = budget;
  let remainingPicks = slotsP + slotsM;

  const buildSuggestion = (pick, planPriority, planNote) => ({
    id: pick.id,
    name: pick.name,
    team: pick.team,
    role: pick.role,
    currentCaps: pick.currentCaps,
    previousCaps: pick.previousCaps,
    value: pick.value,
    fmarkavg: pick.fmarkavg,
    estimatedPrice: pick.price,
    planPriority,
    planNote,
  });

  // ── Portieri: titolare (preferibilmente di una big) + riserve stessa squadra
  //    o segnaposto, secondo la strategia dedicata ────────────────────────────
  const keeperFirstCap = Math.max(1, Math.round(budget * 0.18));
  for (let i = 0; i < slotsP; i++) {
    const maxSpend = remainingBudget - (remainingPicks - 1);
    remainingPicks -= 1;
    if (maxSpend < 1) continue;

    const isFirst = ownedKeepers === 0 && i === 0;

    if (!isFirst) {
      // Riserva: stessa squadra del titolare top, o segnaposto se ambigua
      if (keeperAnchorTeamKey) {
        const anchorList = eliteKeepersByTeam.get(keeperAnchorTeamKey) || [];
        const freeBackups = anchorList.slice(1).filter((p) => !isOwnedPlayer(p, ownedIndex));
        if (freeBackups.length > 2) {
          const teamName = getAiPlayerTeam(anchorList[0]);
          const ordinal = ownedKeepers + suggestions.P.length + 1;
          remainingBudget -= 1;
          suggestions.P.push(buildKeeperPlaceholderSuggestion(ordinal, teamName));
          continue;
        }
        const anchorKey = keeperAnchorTeamKey;
        const backup = keeperAgents
          .filter((c) => !pickedIds.has(String(c.id)))
          .filter((c) => normalizeTeamKey(c.team) === anchorKey && c.price <= maxSpend)
          .sort((a, b) => b.value - a.value)[0];
        if (backup) {
          pickedIds.add(String(backup.id));
          remainingBudget -= backup.price;
          suggestions.P.push(buildSuggestion(backup, 3, 'Riserva P'));
          continue;
        }
      }
      const cheap = keeperAgents
        .filter((c) => !pickedIds.has(String(c.id)))
        .filter((c) => c.price <= maxSpend)
        .sort((a, b) => (a.price - b.price) || (b.strongScore - a.strongScore))[0];
      if (cheap) {
        pickedIds.add(String(cheap.id));
        remainingBudget -= cheap.price;
        suggestions.P.push(buildSuggestion(cheap, 3, 'Riserva P'));
      }
      continue;
    }

    // Titolare (entro un tetto del budget), preferendo una big
    const cap = Math.min(maxSpend, keeperFirstCap);
    let eligible = keeperAgents.filter((c) => !pickedIds.has(String(c.id))).filter((c) => c.price <= cap);
    if (!eligible.length) eligible = keeperAgents.filter((c) => !pickedIds.has(String(c.id))).filter((c) => c.price <= maxSpend);
    if (!eligible.length) continue;
    const elite = eligible.filter((c) => isTopEliteKeeper(c.name, normalizeTeamKey(c.team)));
    const poolK = elite.length ? elite : eligible;
    const pick = poolK.sort((a, b) => (b.strongScore - a.strongScore) || (a.price - b.price))[0];
    pickedIds.add(String(pick.id));
    remainingBudget -= pick.price;
    const tk = normalizeTeamKey(pick.team);
    if (isTopEliteKeeper(pick.name, tk)) keeperAnchorTeamKey = tk;
    suggestions.P.push(buildSuggestion(pick, 1, 'Portiere titolare'));
  }

  // ── Movimento: selezione guidata dalla copertura ────────────────────────────
  const planTokenSets = [...movementRoster];
  const planEntries = [];
  // Titolari da coprire nel modulo primario: le prime scelte utili li completano
  let startersToLabel = target.movementSlots.length - target.match.matched;

  // Budget "a rampa": i primi acquisti (titolari) pesano di più delle riserve
  const rampWeight = (i) => Math.max(0.6, 3 - i * 0.28);
  let remainingWeight = 0;
  for (let i = 0; i < slotsM; i++) remainingWeight += rampWeight(i);

  for (let i = 0; i < slotsM; i++) {
    const weight = rampWeight(i);
    const share = remainingWeight > 0 ? (remainingBudget * weight) / remainingWeight : remainingBudget;
    remainingWeight -= weight;
    const maxSpend = remainingBudget - (remainingPicks - 1);
    remainingPicks -= 1;
    if (maxSpend < 1) continue;

    const capLimit = Math.max(1, Math.round(Math.min(share, maxSpend)));

    const pcFull = pcCount >= MANTRA_PC_MAX;
    const eligibleAt = (limit) => movementAgents
      .filter((c) => !pickedIds.has(String(c.id)))
      .filter((c) => !(isPcCandidate(c) && pcFull))
      .filter((c) => c.price <= limit);

    let pool = eligibleAt(capLimit);
    if (!pool.length) pool = eligibleAt(maxSpend);
    if (!pool.length) continue;

    // Limita i candidati valutati (costo del matching) mantenendo i più utili
    if (pool.length > 80) {
      pool = [...pool].sort((a, b) => (b.strongScore + b.benchScore) - (a.strongScore + a.benchScore)).slice(0, 80);
    }

    // Scelta: massimizza il guadagno di copertura; a parità, qualità e prezzo
    const base = robustness(planTokenSets);
    let best = null;
    let bestScore = -Infinity;
    let bestGain = 0;
    for (const c of pool) {
      const gain = robustness([...planTokenSets, c.tokens]) - base;
      const score = (gain * 100) + (c.strongScore * 0.5) - (c.price * 0.4);
      if (score > bestScore) { bestScore = score; best = c; bestGain = gain; }
    }
    if (!best) continue;

    pickedIds.add(String(best.id));
    remainingBudget -= best.price;
    planTokenSets.push(best.tokens);
    if (isPcCandidate(best)) pcCount += 1;

    // I primi acquisti che fanno progredire la copertura sono i titolari
    const isStarter = startersToLabel > 0 && bestGain > 0;
    if (isStarter) startersToLabel -= 1;
    const suggestion = buildSuggestion(
      best,
      isStarter ? 0 : 2,
      isStarter ? `Titolare · ${target.label}` : 'Copertura ruoli'
    );
    suggestions.M.push(suggestion);
    planEntries.push({ pick: best, suggestion });
  }

  // ── Secondo giro: spendi i crediti avanzati migliorando la qualità SENZA
  //    peggiorare la copertura (la robustezza non deve calare) ────────────────
  let leftover = Math.max(0, remainingBudget);

  let improved = true;
  while (improved && leftover > 0) {
    improved = false;
    for (const entry of planEntries) {
      const others = [...movementRoster, ...planEntries.filter((e) => e !== entry).map((e) => e.pick.tokens)];
      const baseRob = robustness([...others, entry.pick.tokens]);
      const maxPrice = entry.pick.price + leftover;
      const pcBudget = MANTRA_PC_MAX - pcCount + (isPcCandidate(entry.pick) ? 1 : 0);

      let candidates = movementAgents
        .filter((c) => !pickedIds.has(String(c.id)))
        .filter((c) => c.price <= maxPrice)
        .filter((c) => !isPcCandidate(c) || pcBudget >= 1)
        .filter((c) => c.strongScore > entry.pick.strongScore);
      if (candidates.length > 60) {
        candidates = [...candidates].sort((a, b) => b.strongScore - a.strongScore).slice(0, 60);
      }

      const best = candidates
        .filter((c) => robustness([...others, c.tokens]) >= baseRob)
        .sort((a, b) => (b.strongScore - a.strongScore) || (a.price - b.price))[0];

      if (!best) continue;

      pcCount = pcCount - (isPcCandidate(entry.pick) ? 1 : 0) + (isPcCandidate(best) ? 1 : 0);
      pickedIds.delete(String(entry.pick.id));
      pickedIds.add(String(best.id));
      leftover -= (best.price - entry.pick.price);
      Object.assign(entry.suggestion, {
        id: best.id,
        name: best.name,
        team: best.team,
        role: best.role,
        currentCaps: best.currentCaps,
        previousCaps: best.previousCaps,
        value: best.value,
        fmarkavg: best.fmarkavg,
        estimatedPrice: best.price,
      });
      entry.pick = best;
      improved = true;
    }
  }

  return suggestions;
}

// ─── SUGGERIMENTI CLASSIC ─────────────────────────────────────────────────────
// Piano d'acquisto coordinato: i titolari (numero per ruolo dai moduli più usati,
// 433 e 343) ricevono la fetta grossa del budget e sono scelti per qualità/valore,
// non per il solo minutaggio; le riserve restano economiche e la porta segue la
// stessa strategia "squadra top" del mantra. I crediti avanzati vengono spesi
// migliorando i titolari.

// Titolari per ruolo = unione dei moduli più giocati 433 (D4 C3 A3) e 343 (D3 C4 A3)
const CLASSIC_TITOLARI_BY_ROLE = { P: 1, D: 4, C: 4, A: 3 };
// Peso di budget per ogni slot titolare: l'attacco pesa di più
const CLASSIC_STARTER_WEIGHT = { A: 5, C: 3.4, D: 2.6, P: 2.0 };

function buildClassicCandidate(p, role) {
  const priceInfo = getAiPriceEstimate(p, role);
  const value = getAiPlayerValue(p);
  const price = Math.max(1, Math.round(v(priceInfo.estimatedPrice)));
  const currentCaps = getAiPlayerCaps(p);
  const previousCaps = getAiPrevCaps(p);
  const fmv = getAiPlayerFmv(p);
  const prevFmv = getAiPrevFmv(p);
  const mv = getAiPlayerMv(p);
  const prevMv = getAiPrevMv(p);
  const perf = v(priceInfo.performanceScore);

  // fmv aveva già un fallback fisso sulla stagione precedente (13/6), ma
  // sommava entrambi sempre — anche a stagione piena, contando due volte lo
  // stesso segnale. mv invece non aveva alcun fallback. Ora entrambi usano
  // pesi che si ridistribuiscono sullo storico solo quando mancano presenze
  // in stagione corrente, e restano identici a prima una volta che i dati
  // di quest'anno sono sufficienti.
  const { currentWeight: fmvWeight, previousWeight: prevFmvWeight } =
    getSeasonBlendWeights(13, 6, currentCaps);
  const { currentWeight: mvWeight, previousWeight: prevMvWeight } =
    getSeasonBlendWeights(5, 0, currentCaps);
  const { currentWeight: benchFmvWeight, previousWeight: benchPrevFmvWeight } =
    getSeasonBlendWeights(7, 0, currentCaps);

  // Titolare: qualità e valore assoluti contano più delle sole presenze,
  // così emergono i big da pagare (non i workhorse low-cost)
  const starterScore =
    (value * 7) +
    (fmv * fmvWeight) + (prevFmv * prevFmvWeight) +
    (mv * mvWeight) + (prevMv * prevMvWeight) +
    (perf * 1.6) + (currentCaps * 4) + (previousCaps * 2);

  // Riserva: gioca, costa poco, affidabile
  const bargain = price <= 1 ? 300 : price <= 3 ? 200 : price <= 6 ? 110 : price <= 10 ? 40 : 0;
  const benchScore =
    (currentCaps * 24) + (previousCaps * 11) +
    (fmv * benchFmvWeight) + (prevFmv * benchPrevFmvWeight) +
    (perf * 0.8) + bargain - (price * 11);

  // Probabile titolare/panchina nella prossima giornata: piccola spinta o
  // freno su entrambi i punteggi, non un fattore dominante (vedi commento
  // su getPlayProbabilityScoreFactor).
  const probFactor = getPlayProbabilityScoreFactor(p.playProbability);

  return {
    id: p.id,
    name: p.name || p.playerName || '—',
    team: getAiPlayerTeam(p),
    role,
    price,
    value,
    currentCaps,
    previousCaps,
    fmarkavg: fmv,
    starterScore: starterScore * probFactor,
    benchScore: benchScore * probFactor,
  };
}

export function getClassicSmartSuggestions({
  players = [],
  purchases = [],
  myRosterPurchases = [],
  teamStats = {},
} = {}) {
  const suggestionsByRole = { P: [], D: [], C: [], A: [] };

  const budget = Math.max(v(teamStats?.remaining), 0);
  const remainingByRole = teamStats?.remainingByRole || {};
  const totalSlots = ROLE_ORDER.reduce((a, r) => a + v(remainingByRole?.[r] || 0), 0);
  if (totalSlots <= 0 || budget <= 0) return suggestionsByRole;

  const ownedIndex = buildOwnedPlayerIndex(purchases);
  const ranking = buildEliteKeeperRanking(players);

  const ownedKeepers = (myRosterPurchases || [])
    .filter((p) => !p?.deleted)
    .filter((p) => isAiGoalkeeper(p?.playerSnapshot || p, 'classic') || isAiGoalkeeper(p?.playerSnapshot || p, 'mantra'))
    .length;

  const myEliteKeeperTeamKeys = new Set(
    (myRosterPurchases || [])
      .filter((p) => !p?.deleted)
      .filter((p) => isAiGoalkeeper(p?.playerSnapshot || p, 'classic') || isAiGoalkeeper(p?.playerSnapshot || p, 'mantra'))
      .map((p) => normalizeTeamKey(getAiPlayerTeam(p?.playerSnapshot || p)))
      .filter((key) => AI_ELITE_KEEPER_TEAMS.has(key))
  );
  const iOwnEliteKeeper = myEliteKeeperTeamKeys.size > 0;

  // Se possiedo il titolare di una big → aggancio le sue riserve
  const anchor = getMyEliteKeeperAnchor({ players, purchases, myRosterPurchases });
  let keeperAnchorTeamKey = anchor ? anchor.teamKey : null;

  // Riserve non identificabili con certezza (più di due libere) → segnaposto
  const anchorAmbiguous = (teamKey) =>
    Boolean(teamKey) && eliteKeeperFreeBackups(ranking, teamKey, ownedIndex).length > 2;

  // ── Candidati per ruolo ─────────────────────────────────────────────────────
  const roleCandidates = { P: [], D: [], C: [], A: [] };
  (players || [])
    .filter((p) => !p?.deleted)
    .filter((p) => !isOwnedPlayer(p, ownedIndex))
    .forEach((p) => {
      const role = getAiRoleFamily(p, 'classic');
      if (!roleCandidates[role]) return;
      // I portieri di riserva (0 presenze) restano validi per la strategia porta
      if (role !== 'P' && !hasPlayableHistory(p)) return;
      roleCandidates[role].push(buildClassicCandidate(p, role));
    });

  // ── Bisogni ─────────────────────────────────────────────────────────────────
  const needs = [];
  ROLE_ORDER.forEach((role) => {
    const slots = v(remainingByRole?.[role] || 0);
    if (slots <= 0) return;

    if (role === 'P') {
      for (let i = 0; i < slots; i++) {
        const isFirst = ownedKeepers === 0 && i === 0;
        needs.push({ role: 'P', kind: isFirst ? 'keeperStarter' : 'keeperReserve', weight: isFirst ? 2.0 : 0.5 });
      }
      return;
    }

    const starterSlots = Math.min(CLASSIC_TITOLARI_BY_ROLE[role], slots);
    for (let i = 0; i < starterSlots; i++) needs.push({ role, kind: 'starter', weight: CLASSIC_STARTER_WEIGHT[role] });
    for (let i = 0; i < slots - starterSlots; i++) needs.push({ role, kind: 'bench', weight: 0.5 });
  });

  // Titolari prima (per peso), poi portiere titolare, poi riserve e portieri riserva
  const orderedNeeds = [
    ...needs.filter((n) => n.kind === 'starter').sort((a, b) => b.weight - a.weight),
    ...needs.filter((n) => n.kind === 'keeperStarter'),
    ...needs.filter((n) => n.kind === 'bench'),
    ...needs.filter((n) => n.kind === 'keeperReserve'),
  ];

  // ── Allocazione greedy del budget ───────────────────────────────────────────
  const pickedIds = new Set();
  const planEntries = [];
  let remainingBudget = budget;
  let remainingWeight = orderedNeeds.reduce((s, n) => s + n.weight, 0);
  let remainingNeeds = orderedNeeds.length;

  orderedNeeds.forEach((need) => {
    const cap = remainingWeight > 0 ? (remainingBudget * need.weight) / remainingWeight : 1;
    const maxSpend = remainingBudget - (remainingNeeds - 1);
    remainingWeight -= need.weight;
    remainingNeeds -= 1;
    if (maxSpend < 1) return;

    // Riserva portiere con riserve ambigue → segnaposto da 1 credito
    if (need.kind === 'keeperReserve' && keeperAnchorTeamKey && anchorAmbiguous(keeperAnchorTeamKey)) {
      const teamName = getAiPlayerTeam(ranking.get(keeperAnchorTeamKey)[0]);
      const ordinal = ownedKeepers + suggestionsByRole.P.length + 1;
      remainingBudget -= 1;
      suggestionsByRole.P.push(buildKeeperPlaceholderSuggestion(ordinal, teamName));
      return;
    }

    const pool = roleCandidates[need.role].filter((c) => !pickedIds.has(String(c.id)));
    let pick = null;

    // Riserva portiere: 2°/3° per quota della stessa squadra del titolare top
    if (need.kind === 'keeperReserve' && keeperAnchorTeamKey) {
      pick = pool
        .filter((c) => normalizeTeamKey(c.team) === keeperAnchorTeamKey)
        .filter((c) => c.price <= maxSpend)
        .sort((a, b) => b.value - a.value)[0] || null;
    }

    if (!pick) {
      const capLimit = Math.max(1, Math.round(Math.min(cap, maxSpend)));
      const useStrong = need.kind === 'starter' || need.kind === 'keeperStarter';

      let eligible = pool.filter((c) => c.price <= capLimit);
      if (!eligible.length) eligible = pool.filter((c) => c.price <= maxSpend);

      if (need.kind === 'keeperStarter' && !iOwnEliteKeeper) {
        // Preferisci il portiere titolare di una big (sblocca riserve low cost)
        const elite = eligible.filter((c) => isTopEliteKeeperName(ranking, normalizeTeamKey(c.team), c.name));
        if (elite.length) eligible = elite;
      } else if (need.kind === 'keeperStarter' && iOwnEliteKeeper) {
        // Ho già il titolare di una big: non prendere quello di un'ALTRA big
        eligible = eligible.filter((c) => {
          const tk = normalizeTeamKey(c.team);
          return !(AI_ELITE_KEEPER_TEAMS.has(tk) && !myEliteKeeperTeamKeys.has(tk) && isTopEliteKeeperName(ranking, tk, c.name));
        });
      }

      if (!eligible.length) return;

      eligible.sort((a, b) => useStrong
        ? (b.starterScore - a.starterScore) || (b.price - a.price)
        : (b.benchScore - a.benchScore) || (a.price - b.price));
      pick = eligible[0];
    }

    if (!pick) return;

    pickedIds.add(String(pick.id));
    remainingBudget -= pick.price;

    // Ho scelto il portiere titolare di una big → aggancia le riserve
    if (need.kind === 'keeperStarter') {
      const tk = normalizeTeamKey(pick.team);
      if (isTopEliteKeeperName(ranking, tk, pick.name)) keeperAnchorTeamKey = tk;
    }

    const suggestion = {
      id: pick.id,
      name: pick.name,
      team: pick.team,
      role: pick.role,
      currentCaps: pick.currentCaps,
      previousCaps: pick.previousCaps,
      value: pick.value,
      fmarkavg: pick.fmarkavg,
      estimatedPrice: pick.price,
    };
    suggestionsByRole[need.role].push(suggestion);
    planEntries.push({ role: need.role, pick, suggestion, upgradable: need.kind === 'starter', weight: need.weight });
  });

  // ── Secondo giro: spendi i crediti avanzati migliorando i titolari ─────────
  let leftover = Math.max(0, remainingBudget);
  const upgradeOrder = [...planEntries].filter((e) => e.upgradable).sort((a, b) => b.weight - a.weight);

  let improved = true;
  while (improved && leftover > 0) {
    improved = false;
    for (const entry of upgradeOrder) {
      const maxPrice = entry.pick.price + leftover;
      const best = roleCandidates[entry.role]
        .filter((c) => !pickedIds.has(String(c.id)))
        .filter((c) => c.price <= maxPrice)
        .sort((a, b) => (b.starterScore - a.starterScore) || (b.price - a.price))[0];

      if (!best || best.starterScore <= entry.pick.starterScore) continue;

      pickedIds.delete(String(entry.pick.id));
      pickedIds.add(String(best.id));
      leftover -= (best.price - entry.pick.price);

      Object.assign(entry.suggestion, {
        id: best.id,
        name: best.name,
        team: best.team,
        role: best.role,
        currentCaps: best.currentCaps,
        previousCaps: best.previousCaps,
        value: best.value,
        fmarkavg: best.fmarkavg,
        estimatedPrice: best.price,
      });
      entry.pick = best;
      improved = true;
    }
  }

  return suggestionsByRole;
}

export function getSmartSuggestions({
  players = [],
  purchases = [],
  myRosterPurchases = [],
  auction = null,
  teamStats = {},
  totalTeams = 0,
  playProbabilityById = {},
} = {}) {
  const rolesMode =
    teamStats?.remainingByRole &&
    Object.prototype.hasOwnProperty.call(teamStats.remainingByRole, 'M')
      ? 'mantra'
      : 'classic';

  // Arricchisce i giocatori con la probabilità di impiego (prossima giornata,
  // fonte FantaMaster) così i punteggi titolare/riserva dei candidati ne
  // tengono conto — vedi getPlayProbabilityScoreFactor.
  const hasProbData = playProbabilityById && Object.keys(playProbabilityById).length > 0;
  const enrichedPlayers = hasProbData
    ? players.map((p) => {
      const entry = playProbabilityById[String(p.id)];
      return entry ? { ...p, playProbability: entry } : p;
    })
    : players;

  if (rolesMode === 'mantra') {
    return getMantraSmartSuggestions({ players: enrichedPlayers, purchases, myRosterPurchases, teamStats });
  }

  return getClassicSmartSuggestions({ players: enrichedPlayers, purchases, myRosterPurchases, teamStats });
}

export function buildFormationPlan(roster, presetKey) {
  const preset = FORMATIONS[presetKey] || FORMATIONS['4-3-3'];
  const grouped = { P: [], D: [], C: [], A: [] };

  (roster || []).forEach((purchase) => {
    const role = purchase?.playerSnapshot?.role || purchase?.role || 'A';
    if (!grouped[role]) grouped[role] = [];
    grouped[role].push({
      ...purchase,
      score: calcPlayerScore(purchase),
    });
  });

  const starting = {};
  const bench = [];

  ROLE_ORDER.forEach((role) => {
    const needed = preset[role] || 0;
    const sorted = [...(grouped[role] || [])].sort((a, b) => b.score - a.score);
    starting[role] = sorted.slice(0, needed);
    bench.push(...sorted.slice(needed));
  });

  const missingByRole = ROLE_ORDER.reduce((acc, role) => {
    const needed = preset[role] || 0;
    const have = (starting[role] || []).length;
    acc[role] = Math.max(needed - have, 0);
    return acc;
  }, {});

  return {
    preset,
    starting,
    bench,
    missingByRole,
    complete: Object.values(missingByRole).every((n) => n === 0),
    totalNeeded: Object.values(preset).reduce((a, b) => a + b, 0),
  };
}

export function groupPurchasesByRole(list = []) {
  const grouped = { P: [], D: [], C: [], A: [] };

  (list || []).forEach((purchase) => {
    const role = purchase?.playerSnapshot?.role || purchase?.role || 'A';
    if (!grouped[role]) grouped[role] = [];
    grouped[role].push(purchase);
  });

  ROLE_ORDER.forEach((role) => {
    grouped[role].sort((a, b) =>
      (a.playerSnapshot?.name || a.playerName || '').localeCompare(
        b.playerSnapshot?.name || b.playerName || ''
      )
    );
  });

  return grouped;
}

export function formatTeamCardProgress(stats, totalCredits) {
  return Math.min(
    (((v(totalCredits) || 0) - v(stats?.remaining || 0)) / Math.max(v(totalCredits), 1)) * 100,
    100
  );
}